<template>
  <div class="auth-ratings">
    <div class="auth-content ratings">
      <div class="auth-context left">
        <span class="auth-title">Your chess</span>
        <span class="subtitle"></span>
      </div>
      <form class="auth-content-wrapper" @submit="handleSubmit">
        <div class="slot-content">
          <gam-ratings :is-auth="true" />
        </div>
        <input type="submit" class="hidden" />
      </form>
    </div>

    <div class="auth-methods-group">
      <transition name="dropdown" mode="out-in">
        <GamButtonMessage class="error-message" v-if="errorMessage" v-bind="errorMessage" />
      </transition>

      <div class="methods-row">
        <gam-button v-bind="getContinueButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterAuthType } from '@/core/data/config/uiConfig.interface';
import { gambits } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { useRatingStore } from '@/stores/rating.store';
import GamButton from '@/views/components/GamButton.vue';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamRatings from '@/views/components/gam-ratings/GamRatings.vue';
import useLichess from '@/views/composables/auth/social/useLichess';
import {
  GamButtonMessageVariant,
  GamButtonSize,
  GamButtonVariant,
} from '@/views/composables/constants/components/gamButton.constants';
import type { GamButtonMessageType, GamButtonType } from '@/views/composables/models/components/GamButton';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const ratingStore = useRatingStore();
const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const { ratings } = storeToRefs(ratingStore);
const isSubmitting = ref<boolean>(false);
const errorMessage = ref<GamButtonMessageType | null>(null);
const { parseLichessRatings } = useLichess();

const router = useRouter();
const route = useRoute();

onBeforeMount(async () => {
  await authStore.fetchUserInfo(true);

  const { firstName, lastName, birthDate, username, emoji, rating } = userDetails.value || {};

  // Require login
  if (!authStore.isAuthenticated()) {
    return router.push('/auth');
  }
  // Return to personal details step
  if (!firstName || !lastName || !birthDate || !username || !emoji) {
    return router.push('/auth/registration');
  }
  // Nothing left to ask when the API already resolved a rating, which is the case for a
  // Lichess signup. A Lichess connect redirect still has to land here so GamRatings can
  // persist the ratings it carries in the query.
  if (rating && !parseLichessRatings(route)) {
    return router.push({ name: RouterAuthType.AUTH_LOCATION });
  }
});

onMounted(() => {
  document.querySelector('.auth-modal')?.classList.add('ratings');
});

onUnmounted(() => {
  document.querySelector('.auth-modal')?.classList.remove('ratings');
});

async function handleSubmit(event: Event) {
  event.preventDefault();

  if (isSubmitting.value) return;

  ratingStore.clampGambitsRating();
  errorMessage.value = null;

  isSubmitting.value = true;
  const result = await gambits.authService.submitRatings(ratings.value);

  if (result.error) {
    errorMessage.value = {
      label: result.error.message,
      variant: GamButtonMessageVariant.ERROR,
    };

    isSubmitting.value = false;
    return;
  }

  await authStore.storeLocation();
  await authStore.storePushToken();
  await authStore.authorizeFirebase();
  // await authStore.setUser(result.data);

  await router.push({ name: RouterAuthType.AUTH_LOCATION });

  isSubmitting.value = false;
}

const getContinueButton = computed((): GamButtonType => {
  return {
    variant: GamButtonVariant.PRIMARY,
    size: GamButtonSize.LARGE,
    label: 'auth.page.ratings.continue.button.label',
    isFullSize: true,
    isCentered: true,
    isDisabled: !ratingStore.isRatingAdded() || isSubmitting.value,
    isLoading: isSubmitting.value,
    onClick: handleSubmit,
  };
});
</script>

<style lang="scss">
// .auth-modal.ratings {
//   max-width: 353px !important;
// }
</style>

<style scoped lang="scss">
.ratings {
  padding-bottom: var(--spacing-l) !important;
}

.error-message {
  margin-top: calc(var(--spacing-xl) * -1);
}

.slot-content,
.auth-content-wrapper {
  width: 100%;
}
</style>
